<script setup lang="ts">
import { CheckCircle, XCircle, Printer, Info } from "lucide-vue-next";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { isStoreOrderAllowedToCancel, isStoreOrderAllowedToAccept } from "@lib/types/storeOrderStatus";
import { printOrder } from "./printHTML";
import ShowStoreOrder from "@/orders-manager/order-modal/ShowStoreOrder.vue";
import PrintOptionsDropdown from "./PrintOptionsDropdown.vue";
import { handleAccept, isAcceptingOrder } from "./handleAccept";
import { handleCancel, isCancellingOrder } from "./handleCancel";
import { STRIPE_PAYMENT_TYPE_ID } from "@lib/consts/paymentIds";
import { PAYPAL_PAYMENT_TYPE_ID } from "@lib/consts/paymentIds";
import { computed } from "vue";
import { cachedStoreOrders } from "./RecentOrdersStore";

const props = defineProps<{
    storeOrderId: string;
}>();

const order = computed(() => cachedStoreOrders.value[props.storeOrderId]);

const showRefundInfo = computed(() => {
    const o = order.value;
    if (!o) return false;
    return o.paymentMethodJSON?.id === STRIPE_PAYMENT_TYPE_ID ||
        o.paymentMethodJSON?.id === PAYPAL_PAYMENT_TYPE_ID;
});

const browserLocale = window.navigator.language;

const IS_DEV = import.meta.env.DEV;

</script>
<template>
    <div v-if="order">
        <DialogHeader>
            <DialogTitle></DialogTitle>
            <DialogDescription />
            <div class="flex gap-2 justify-between">
                <Button class="w-full text-xl font-bold animate__tada"
                        :disabled="isAcceptingOrder"
                        v-if="isStoreOrderAllowedToAccept(order.orderStatus)"
                        @click="handleAccept(order.storeOrderId, order.storeId)">
                    <CheckCircle class="h-4 w-4 mr-1" />
                    Accept
                </Button>
            </div>
            <div class="flex gap-1 items-center">
                <Button variant="secondary"
                        class="w-full text-xl font-bold"
                        @click="printOrder(order, browserLocale)">
                    <Printer class="mr-1" />
                    Print
                </Button>
                <PrintOptionsDropdown />
            </div>
        </DialogHeader>


        <ShowStoreOrder :orderDetails="order" />

        <div class="flex gap-2 justify-between">
            <Button class="w-full animate__tada"
                    :disabled="isAcceptingOrder"
                    v-if="isStoreOrderAllowedToAccept(order.orderStatus)"
                    @click="handleAccept(order.storeOrderId, order.storeId)">
                <CheckCircle class="h-4 w-4 mr-1" />
                Accept
            </Button>
            <!-- <br>
                                            order.userId: {{ order.userId }}
                                            <br>
                                            user.id: {{ user?.id }}
                                            <br> -->
            <div class="flex gap-2 items-center">
                <DropdownMenu v-if="isStoreOrderAllowedToCancel(order.orderStatus)">
                    <DropdownMenuTrigger as-child>
                        <Button variant="destructive"
                                size="sm">
                            <XCircle /> {{ showRefundInfo ? 'Refund' : 'Cancel' }} Order
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuItem :disabled="isCancellingOrder"
                                          @click="handleCancel(order.storeOrderId, order.storeId)"
                                          class="bg-destructive text-destructive-foreground">
                            <div>
                                <p class="text-2xl font-bold">Are you sure?</p>
                            </div>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <Popover v-if="isStoreOrderAllowedToCancel(order.orderStatus)">
                    <PopoverTrigger as-child>
                        <Info class="inline-block" />
                    </PopoverTrigger>
                    <PopoverContent align="start">
                        <div class="space-y-2">
                            <p class="text-2xl font-bold">Are you sure to cancel?</p>
                            <p class="text-xs my-4">
                                If it was a test order,
                                <br>
                                make sure to cancel it in the same browser.
                                <br>
                                Order Taking app can not detect test orders.
                            </p>
                            <p class="text-xs my-4">
                                Use cancellation wisely,
                                <br>
                                only 5% of orders can be cancelled free.
                            </p>
                            <p class="text-xs my-4">
                                Fees will not be refunded
                                <br>
                                if more than 5% of orders were
                                <br>
                                cancelled in the last 30 days.
                            </p>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            <a class="text-xs underline"
               v-if="order.paymentId === STRIPE_PAYMENT_TYPE_ID &&
                order.stripeStoreConnectAccountId &&
                order.stripePaymentIntentId"
               :href="`https://dashboard.stripe.com/${order.stripeStoreConnectAccountId}/${IS_DEV ? `test/` : ``}payments/${order.stripePaymentIntentId}`"
               target="_blank">
                See payment in stripe dashboard
            </a>
        </div>
    </div>
</template>